import { useRef } from 'react';
import styled from 'styled-components';

import MissingChartIcon from '@/assets/icons/MissingChartIcon';
import { CenterFlex, Flex, VFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Empty from '@/components/Empty/Empty';
import { Stage } from '@/components/Stage/Stage.tsx';
import Typo from '@/components/Typography/Typo';
import { HeySynclyMessageDisplay } from '@/entities/hey-syncly';
import { radius } from '@/styles/sizes';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { AnalysisState, type AnalysisT } from '../Analysis/types';
import DashboardCardStyles from '../Dashboard/components/styles/DashboardCardStyles';

const { StyledDashboardCard, StyledDashboardCardWrapper } = DashboardCardStyles;

type Props = {
  analysis: AnalysisT;
  chartName: string;
};

const StaticDashboard2AnalysisBox = (props: Props) => {
  const { analysis, chartName } = props;
  const chatMessageContainerRef = useRef<HTMLDivElement>(null);
  return (
    <StyledDashboardCardWrapper
      width="auto"
      isDragging={false}
      style={{ height: '100%' }}
    >
      <StyledDashboardCard>
        <Style>
          <DashboardCardHeader>
            <DashboardCardHeaderReferenceSection g={4}>
              <Typo type="label4" weight="r" multilineEllipsis={2}>
                Linked with
              </Typo>
              <Typo type="label4" weight="m" multilineEllipsis={2}>
                {chartName}
              </Typo>
            </DashboardCardHeaderReferenceSection>
            <Flex f={1} j="space-between" a="center" padding="0 24px">
              <VFlex
                g={8}
                f={1}
                style={{
                  cursor: 'pointer',
                }}
              >
                <Typo type="body0" weight="m" multilineEllipsis={2}>
                  {analysis.title}
                </Typo>
              </VFlex>
            </Flex>
          </DashboardCardHeader>
          <VFlex
            f={1}
            className="content-wrapper"
            ref={chatMessageContainerRef}
          >
            <Stage className="flex flex-col flex-1">
              <Stage.Scene
                status={analysis.state === AnalysisState.DISCONNECTED}
              >
                <CenterFlex f={1} d="column">
                  <Empty
                    className="w-62.5 flex-1 self-center"
                    image={
                      <MissingChartIcon
                        width="83px"
                        style={{ marginBottom: -8 }}
                      />
                    }
                    title="Lost connection to the chart"
                    description="The data connection has been lost as the chart has changed."
                    action={{
                      option: 'secondary',
                      text: 'Ask new question',
                      onClick: () => {},
                    }}
                  />
                </CenterFlex>
              </Stage.Scene>
              <Stage.Scene
                status={analysis.state === AnalysisState.IN_PROGRESS}
              >
                <CenterFlex f={1}>
                  <Spinner />
                </CenterFlex>
              </Stage.Scene>
              <Stage.Scene status={analysis.state === AnalysisState.OK}>
                <VFlex f={1}>
                  {analysis.chat && (
                    <HeySynclyMessageDisplay
                      chat_id={analysis.chat.alias}
                      messages={analysis.chat.messages}
                      status={analysis.chat.status}
                      report_alias={analysis.alias}
                      answer={null}
                      onStudioItemClick={() => {}}
                      container={chatMessageContainerRef.current}
                      hideFeedbackCount
                      hideReason
                      hideQuestion
                      hideVote
                    />
                  )}
                </VFlex>
                <Flex className="info-line" j="flex-start">
                  <Typo type="label3">
                    Updated on{' '}
                    {getTimezoneDayjs(analysis.updated_at).format(
                      DATE_FORMAT.readableHour,
                    )}
                  </Typo>
                </Flex>
              </Stage.Scene>
            </Stage>
          </VFlex>
        </Style>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const Style = styled(VFlex)`
  height: 100%;
  .content-wrapper {
    overflow: auto;
    padding: 24px;
    flex: 1;
  }
`;

const DashboardCardHeader = styled(VFlex)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray250};
  border-radius: ${radius.token['3xl']}px;
  height: 88px;
`;

const DashboardCardHeaderReferenceSection = styled(Flex)`
  background-color: ${(props) => props.theme.colors.ND04};
  padding: 8px 16px;
`;

export default StaticDashboard2AnalysisBox;
